import { Formik } from "formik";
import i18n, { TSupportedLanguageKey } from "i18n";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "router";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { errorEntryType } from "@/core/api";
import { refresh, socialRegistration } from "@/data/session";
import { Col, Flex, Row } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { RouteButton } from "@/infrastructure/Route";
import {
  AgreementsForm,
  AGREEMENTS_FORM_INIT_VALUES,
  IAgreementsFormValues,
  validationSchema,
} from "@/views/components/Forms/Agreements";
import { AnimatedLayoutContainer } from "@/views/components/Layout";
import { router } from "@/views/router";
import { useCreateSession } from "@/views/Session/hooks";

interface Props {}

export const AgreementsComponent: FunctionComponent<Props> = () => {
  const { t } = useTranslation("public");
  const {
    matchParams: { pathName },
    searchParams: { registration_token },
    push,
  } = useRouter();
  const { createSession } = useCreateSession();

  const onSubmit = async (values: IAgreementsFormValues) => {
    try {
      await socialRegistration({
        language: i18n.language as TSupportedLanguageKey,
        tokenUUID: registration_token,
        ...values,
      });
      const sessionUser = await refresh();

      await createSession(sessionUser);
    } catch (response) {
      const payload = errorEntryType(response.payload);

      push(router.get(pathName).path, { error: payload.value });
    }
  };

  return (
    <AnimatedLayoutContainer size="md" showLogo>
      <Flex direction="column" padding={["sm", "md", "md", "md"]} innerPadding="lg">
        <Header size="lg" weight="bold" text={t("SESSION_AGREEMENTS.HEADER")} align="center" />
        <Formik
          initialValues={AGREEMENTS_FORM_INIT_VALUES}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          render={form => (
            <FormikForm>
              <Flex direction="column">
                <Row>
                  <Col width={{ xxs: 12 }}>
                    <AgreementsForm form={form} />
                  </Col>
                </Row>

                <Row>
                  <Col width={{ xxs: 12 }}>
                    <Flex direction="row" innerMargin="md" justify="flex-end">
                      <RouteButton
                        size="lg"
                        path={router.get(pathName).path}
                        testName={router.get(pathName).name}
                        variant="secondary">
                        <Text size="lg" color="primary">
                          {t("SESSION_SIGNUP.USER_FORM.BUTTON_CANCEL")}
                        </Text>
                      </RouteButton>
                      <Button onClick={form.submitForm} disabled={form.isSubmitting} size="lg" testName="create">
                        <Text size="lg">{t("SESSION_SIGNUP.USER_FORM.BUTTON_CREATE_ACCOUNT")}</Text>
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Flex>
            </FormikForm>
          )}
        />
      </Flex>
    </AnimatedLayoutContainer>
  );
};
